import { FallbackError } from "@core/domain/errors/fallback.error";
import { ValidationError } from "@core/domain/errors/validation.error";
import { Pagination } from "@core/domain/models/pagination";
import { Either } from "@core/domain/types/either";
import { EntityApiKeysDatasource } from "@entity-api-keys/data/datasources/entity-api-keys.datasource";
import { CreateEntityApiKey } from "@entity-api-keys/domain/models/create-entity-api-key.model";
import {
    CreatedEntityApiKey,
    EntityApiKey,
    EntityApiKeys,
} from "@entity-api-keys/domain/models/entity-api-key.model";
import { PublicApiUsageStats } from "@entity-api-keys/domain/models/public-api-usage-stats.model";
import { UpdateEntityApiKey } from "@entity-api-keys/domain/models/update-entity-api-key.model";
import { inject, injectable } from "inversify";

@injectable()
export class EntityApiKeysRepository {
    constructor(
        @inject(EntityApiKeysDatasource)
        private readonly datasource: EntityApiKeysDatasource,
    ) {}

    async getById(id: number): Promise<Either<FallbackError, EntityApiKey>> {
        return this.datasource.fetchById(id);
    }

    async searchAllBy(
        pagination: Pagination,
        search?: string,
        entityId?: number,
        createdAfter?: string,
        createdBefore?: string,
        isActive?: boolean,
        scopes?: string[],
    ): Promise<Either<FallbackError, EntityApiKeys>> {
        return this.datasource.fetchAllBy(
            pagination,
            search,
            entityId,
            createdAfter,
            createdBefore,
            isActive,
            scopes,
        );
    }

    async create(
        model: CreateEntityApiKey,
    ): Promise<Either<ValidationError | FallbackError, CreatedEntityApiKey>> {
        return this.datasource.create(model);
    }

    async update(
        model: UpdateEntityApiKey,
    ): Promise<Either<ValidationError | FallbackError, EntityApiKey>> {
        return this.datasource.update(model);
    }

    async delete(id: number): Promise<Either<FallbackError, true>> {
        return this.datasource.delete(id);
    }

    async getUsageStats(
        period: string,
    ): Promise<Either<FallbackError, PublicApiUsageStats>> {
        return this.datasource.fetchUsageStats(period);
    }

    async exportUsageStatsXlsx(
        period: string,
    ): Promise<Either<FallbackError, Blob>> {
        return this.datasource.exportUsageStatsXlsx(period);
    }
}
